/**
 * Typdefinitioner för CustomerWizard
 * Wizard för att skapa nya CRM-kunder
 */

import {
  BuildingOfficeIcon,
  UserIcon,
  CurrencyDollarIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline';
import i18n from '@/i18n/config';

export type CustomerType = 'municipality' | 'region' | 'private' | 'partner';
export type CustomerStatus = 'prospect' | 'active';
export type PricingModel = 'monthly' | 'per_vehicle' | 'per_user' | 'custom';
export type ContactRole = 'decision_maker' | 'technical' | 'billing' | 'other';

export interface ContactInfo {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  mobile: string;
  role: string;
}

export interface WizardData {
  // Step 1: Basic info
  name: string;
  org_number: string;
  customer_type: CustomerType;
  status: CustomerStatus;

  // Step 2: Address & Contact
  address: string;
  postal_code: string;
  city: string;
  country: string;
  website: string;

  // Step 3: Contact person
  contact: ContactInfo;

  // Step 4: Pricing
  pricing_model: PricingModel;
  base_price: number;
  price_per_vehicle: number;
  price_per_user: number;
  billing_starts_on: string;
  notes: string;

  // Options
  sendWelcomeEmail?: boolean;
}

export interface WizardStep {
  id: number;
  name: string;
  icon: typeof BuildingOfficeIcon;
}

export const INITIAL_WIZARD_DATA: WizardData = {
  name: '',
  org_number: '',
  customer_type: 'private' as const,
  status: 'prospect',
  address: '',
  postal_code: '',
  city: '',
  country: 'Sverige',
  website: '',
  contact: {
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    mobile: '',
    role: 'decision_maker',
  },
  pricing_model: 'monthly',
  base_price: 0,
  price_per_vehicle: 0,
  price_per_user: 0,
  billing_starts_on: '',
  notes: '',
};

export const WIZARD_STEPS: WizardStep[] = [
  { id: 1, get name() { return i18n.t('copy.hooks.wizardBasicInfo'); }, icon: BuildingOfficeIcon },
  { id: 2, get name() { return i18n.t('copy.hooks.wizardAddress'); }, icon: BuildingOfficeIcon },
  { id: 3, get name() { return i18n.t('copy.hooks.wizardContact'); }, icon: UserIcon },
  { id: 4, get name() { return i18n.t('copy.hooks.wizardPricing'); }, icon: CurrencyDollarIcon },
  { id: 5, get name() { return i18n.t('copy.hooks.wizardSummary'); }, icon: CheckCircleIcon },
];

export function formatPricing(data: WizardData): string {
  switch (data.pricing_model) {
    case 'monthly':
      return i18n.t('copy.hooks.pricePerMonth', { price: data.base_price });
    case 'per_vehicle':
      return i18n.t('copy.hooks.pricePerVehicle', { price: data.price_per_vehicle });
    case 'per_user':
      return i18n.t('copy.hooks.pricePerUser', { price: data.price_per_user });
    case 'custom':
      return i18n.t('copy.hooks.pricingCustom');
    default: {
      const _exhaustive: never = data.pricing_model;
      return _exhaustive;
    }
  }
}
